import React from "react";

/* ============================================================
   Editorial decorative + animated-SVG library
   Pure CSS-driven animations (no client JS needed).
   ============================================================ */

/* Eyebrow + serif heading block used across sections */
export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
  light = false,
  className = "",
}) {
  const alignCls =
    align === "center"
      ? "items-center text-center mx-auto"
      : align === "right"
      ? "items-end text-right ml-auto"
      : "items-start text-left";
  return (
    <div className={`flex flex-col ${alignCls} max-w-3xl ${className}`}>
      {eyebrow && (
        <span
          className={`eyebrow flex items-center gap-3 ${
            light ? "text-gold-soft" : "text-gold-deep"
          }`}
          data-aos="fade-up"
        >
          <span className="h-px w-8 bg-gold/70" />
          {eyebrow}
          {align === "center" && <span className="h-px w-8 bg-gold/70" />}
        </span>
      )}
      <h2
        className={`font-display font-semibold mt-4 text-[clamp(1.9rem,4vw,3.1rem)] leading-[1.08] ${
          light ? "text-white" : "text-ink"
        }`}
        data-aos="fade-up"
        data-aos-delay="80"
      >
        {title}
      </h2>
      {description && (
        <p
          className={`mt-5 text-[15px] md:text-base leading-relaxed ${
            light ? "text-white/80" : "text-ink-soft"
          }`}
          data-aos="fade-up"
          data-aos-delay="150"
        >
          {description}
        </p>
      )}
    </div>
  );
}

/* Soft morphing gradient blobs for section backgrounds */
export function BackdropBlobs({ className = "" }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-brand-300/25 blur-3xl animate-drift" />
      <div className="absolute top-1/3 -right-16 h-80 w-80 rounded-full bg-gold-soft/20 blur-3xl animate-float-slow" />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-brand-200/20 blur-3xl animate-drift" />
    </div>
  );
}

/* Animated flourish divider */
export function FlourishDivider({ className = "" }) {
  return (
    <div className={`flex justify-center ${className}`} aria-hidden>
      <svg width="220" height="26" viewBox="0 0 220 26" fill="none">
        <path
          className="draw-path"
          d="M2 13 H88 M132 13 H218"
          stroke="#61C1E5"
          strokeWidth="1.4"
          strokeLinecap="round"
        />
        <path
          className="draw-path"
          d="M88 13 C98 3, 110 3, 110 13 C110 23, 122 23, 132 13"
          stroke="#1EA7DB"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
        />
        <circle cx="110" cy="13" r="3" fill="#61C1E5" className="animate-float" />
      </svg>
    </div>
  );
}

/* Animated quill drawing an underline — hero accent */
export function AnimatedQuill({ className = "" }) {
  return (
    <svg
      className={className}
      width="120"
      height="120"
      viewBox="0 0 120 120"
      fill="none"
      aria-hidden
    >
      <g className="animate-float-slow" style={{ transformOrigin: "60px 60px" }}>
        <path
          d="M96 22C74 34 52 52 40 78c-4 9-6 16-6 16s8-1 17-6c25-13 43-34 55-56 1-2-2-6-4-6-2 0-3 0-6 2Z"
          fill="#1EA7DB"
        />
        <path
          d="M96 22C74 34 52 52 40 78c14-4 26-14 34-26 8-12 16-24 22-30Z"
          fill="#61C1E5"
        />
        <path d="M34 94c-6 3-14 8-14 8s2-9 6-14c3-4 9-3 11 0 2 3 0 4-3 6Z" fill="#61C1E5" />
      </g>
      <path
        className="draw-path"
        d="M14 104 C40 96, 74 96, 106 104"
        stroke="#61C1E5"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/* Rotating "seal" badge with orbiting text feel */
export function SealBadge({ label = "PRIME • PUBLISHING • AUTHORS • ", className = "" }) {
  const chars = label.split("");
  return (
    <div
      className={`relative flex h-28 w-28 items-center justify-center ${className}`}
      aria-hidden
    >
      <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full animate-spin-slow">
        <defs>
          <path id="seal-circle" d="M60,60 m-42,0 a42,42 0 1,1 84,0 a42,42 0 1,1 -84,0" />
        </defs>
        <text
          fontSize="10.5"
          letterSpacing="2.6"
          fill="#000000"
          fontFamily="var(--font-poppins)"
          fontWeight="600"
        >
          <textPath href="#seal-circle" startOffset="0">
            {label}
          </textPath>
        </text>
      </svg>
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-editorial">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path
            d="M4 5h7a3 3 0 0 1 3 3v11a3 3 0 0 0-3-3H4V5Z"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path
            d="M20 5h-7a3 3 0 0 0-3 3v11a3 3 0 0 1 3-3h7V5Z"
            stroke="#A6DEF2"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

/* Animated open-book line SVG */
export function OpenBookLine({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 200 140"
      fill="none"
      aria-hidden
    >
      <g className="animate-float">
        <path
          className="draw-path"
          d="M100 34C82 22 44 22 22 30v82c22-8 60-8 78 4"
          stroke="#1EA7DB"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          className="draw-path"
          d="M100 34c18-12 56-12 78-4v82c-22-8-60-8-78 4"
          stroke="#61C1E5"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path d="M100 34v82" stroke="#000000" strokeWidth="2" strokeLinecap="round" />
        <g stroke="#61C1E5" strokeWidth="1.4" strokeLinecap="round" opacity="0.7">
          <path d="M36 48h44M36 62h44M36 76h36" />
          <path d="M120 48h44M120 62h44M120 76h36" />
        </g>
      </g>
    </svg>
  );
}

/* Small animated feature-icon frame that wraps any child glyph */
export function GlyphBadge({ children, className = "" }) {
  return (
    <span
      className={`relative inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700 ${className}`}
    >
      <span className="absolute inset-0 rounded-2xl border border-gold/30" />
      <span className="absolute inset-0 rounded-2xl animate-pulse-ring border border-brand-300/50" />
      {children}
    </span>
  );
}

/* Floating paper sheets decoration */
export function FloatingPages({ className = "" }) {
  return (
    <div className={`pointer-events-none absolute ${className}`} aria-hidden>
      <svg width="90" height="110" viewBox="0 0 90 110" fill="none">
        <g className="animate-float-slow">
          <rect x="18" y="16" width="54" height="72" rx="4" fill="#fff" stroke="#61C1E5" strokeWidth="1.2" opacity="0.65" transform="rotate(-8 45 52)" />
          <rect x="14" y="22" width="54" height="72" rx="4" fill="#fff" stroke="#1EA7DB" strokeWidth="1.2" />
          <g stroke="#61C1E5" strokeWidth="1.2" strokeLinecap="round" opacity="0.7">
            <path d="M24 36h34M24 46h34M24 56h24" />
          </g>
        </g>
      </svg>
    </div>
  );
}
